import { useCallback, useMemo } from "react";
import {
  ContactInformationForm,
  useContactInfo,
} from "@apac/components/checkout";
import { PriceCheck } from "@apac/price-check-machine";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CheckoutPage } from "@components/checkout";
import {
  CardPaymentSelectors,
  ContactSelectors,
  FlightPassengerSelectorsV2,
  ParentState,
  getParentState,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, Loading } from "@tenant/ui";
import { CartFulfillComponent } from "../../components/CartFulfillComponent";
import { CartQuoteComponent } from "../../components/CartQuoteComponent";
import { FlightsCheckoutPriceCheck } from "../../components/FlightsCheckoutPriceCheck";
import {
  ConnectedPassengerSelection,
  ConnectedPriceBreakdown,
  ConnectedTripDetails,
} from "../../connectedComponents";
import { ConnectedPaymentInformation } from "../../connectedComponents/PaymentInformation";
import { CheckoutEvent } from "../../events";
import { ConfirmationPage } from "../ConfirmationPage";
import styles from "./styles.module.scss";

// This is for desktop
export const SinglePageCheckout = () => {
  const { t } = useI18nContext();

  const [block, cn] = useModuleBEM(styles, "SinglePageCheckout");

  const [state, send] = useCheckoutState();

  const currentParentState = useMemo(
    () => getParentState(state?.value),
    [state?.value]
  );

  const contactInfoParams = useContactInfo();
  const hasCompletedPassengerInformationStep = useMemo(() => {
    return ![
      ParentState.passengerInformation,
      ParentState.contactInformation,
    ].includes(ParentState[currentParentState]);
  }, [currentParentState]);

  const hasSelectedPayment = !!useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );

  const disabledFulfill = useMemo(() => {
    return !hasCompletedPassengerInformationStep && !hasSelectedPayment;
  }, [hasCompletedPassengerInformationStep, hasSelectedPayment]);

  const startCartFulfill = useCallback(() => {
    send(CheckoutEvent.GO_TO_UPDATE_BEFORE_FULFILL);
  }, []);

  switch (currentParentState) {
    case ParentState.loading:
      return <Loading />;
    case ParentState.bookingConfirmation:
      return <ConfirmationPage />;
    default: {
      return (
        <CheckoutPage className={block} continueButtonDisabled={false}>
          <div className={cn("container")}>
            <div className={cn("main-content")}>
              <ConnectedTripDetails showActionLinks={true} />
              <div className={cn("section")}>
                <div className={cn("section-title")}>
                  {t("checkoutSection.steps.passengerInformationStep")}
                </div>
                <ConnectedPassengerSelection />
                {/* TODO: For desktop this needs to be updated */}
                <div className={cn("form-container")}>
                  <div className={cn("header")}>
                    <div className={cn("title")}>
                      {t("checkoutSection.contactInformation.title")}
                    </div>
                    <div className={cn("subtitle")}>
                      {t("checkoutSection.contactInformation.subtitle")}
                    </div>
                  </div>

                  <ContactInformationForm {...contactInfoParams} />
                </div>
                <CartQuoteContinueOrEdit />
              </div>

              <div
                className={cn("section", {
                  disabled: !hasCompletedPassengerInformationStep,
                })}
              >
                <div className={cn("section-title")}>
                  {t("checkoutSection.steps.paymentStep")}
                </div>

                <ConnectedPaymentInformation
                  disabled={!hasCompletedPassengerInformationStep}
                />
              </div>
            </div>
            <div className={cn("side-content")}>
              <div className={cn("section")}>
                <ConnectedPriceBreakdown showFareDescription />

                <button
                  className={cn("fullfill-button")}
                  disabled={disabledFulfill}
                  onClick={() => {
                    startCartFulfill();
                  }}
                >
                  {t("confirmAndBook")}
                </button>
              </div>
            </div>
          </div>
          {currentParentState === PriceCheck && <FlightsCheckoutPriceCheck />}
          {currentParentState === ParentState.cartQuote ? (
            <CartQuoteComponent />
          ) : null}
          {currentParentState === ParentState.cartFulfill ? (
            <CartFulfillComponent />
          ) : null}
        </CheckoutPage>
      );
    }
  }
};

const CartQuoteContinueOrEdit = () => {
  const [block, cn] = useModuleBEM(styles, "SinglePageCheckout");

  const { t } = useI18nContext();
  const [state, send] = useCheckoutState();

  const isContactStepValid = useSelector(
    ContactSelectors.getContactInformationValidated
  );

  const isPassengerStepValid = !!useSelector(
    FlightPassengerSelectorsV2.getAllSelectedPassengerIds
  )?.length;

  const isFirstStepValid = isContactStepValid && isPassengerStepValid;

  const currentParentState = useMemo(
    () => getParentState(state?.value),
    [state?.value]
  );

  const hasCompletedPassengerInformationStep = useMemo(() => {
    return ![
      ParentState.passengerInformation,
      ParentState.contactInformation,
    ].includes(ParentState[currentParentState]);
  }, [currentParentState]);

  const startCartQuote = useCallback(() => {
    send(CheckoutEvent.GO_TO_CART_QUOTE);
  }, []);

  const goToPassengerInformation = useCallback(() => {
    send(CheckoutEvent.GO_TO_CONTACT_INFORMATION);
  }, []);

  return hasCompletedPassengerInformationStep ? (
    <button className={cn("passenger-edit")} onClick={goToPassengerInformation}>
      {t("passengerInformation.editPassengerInfoText")}{" "}
      <Icon iconName={IconNameEnum.edit} />
    </button>
  ) : (
    <button
      className={cn("cart-quote-continue")}
      disabled={!isFirstStepValid}
      onClick={() => {
        startCartQuote();
      }}
    >
      {t("checkoutSection.passenger.apply")}
    </button>
  );
};
